/**
 * PaymentReminderService.ts
 *
 * Pure helpers for building WhatsApp payment-reminder messages and the
 * wa.me link to send them. No React, no side effects beyond the returned
 * strings — kept this way so both ViewInvoice.tsx today and the Customer
 * page later can import the exact same logic.
 */

export type ReminderLanguage = "english" | "hindi" | "hinglish";

export interface IReminderInvoice {
  invoiceId: string;
  dueAmount: number;
  dueDate?: string; // ISO string, optional
}

const fmtCurrency = (n: number) =>
  `₹${Number(n ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const fmtDateDisplay = (dateStr?: string): string => {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("en-GB"); // dd/mm/yyyy
  } catch {
    return dateStr;
  }
};

const invoiceListText = (invoices: IReminderInvoice[]) =>
  invoices.map((inv) => inv.invoiceId).join(", ");

export const totalDueAmount = (invoices: IReminderInvoice[]) =>
  invoices.reduce((sum, inv) => sum + (Number(inv.dueAmount) || 0), 0);

/**
 * Builds the reminder message body for the given language. Works for one
 * invoice or several (consolidated reminder) — when there's more than one,
 * the invoice list is comma-separated and the amount is the combined total.
 */
export const buildReminderMessage = (
  language: ReminderLanguage,
  customerName: string,
  invoices: IReminderInvoice[],
  businessName: string
): string => {
  const amount = fmtCurrency(totalDueAmount(invoices));
  const invoiceList = invoiceListText(invoices);
  const invoiceWordEn = invoices.length > 1 ? "invoices" : "invoice";

  if (language === "hindi") {
    return [
      `नमस्ते ${customerName} जी,`,
      "",
      `आपके Invoice No. ${invoiceList} की ₹${amount} राशि का भुगतान बाकी है।`,
      "",
      "कृपया अपनी सुविधा के अनुसार लंबित भुगतान करने की कृपा करें।",
      "",
      "धन्यवाद,",
      businessName,
    ].join("\n");
  }

  if (language === "hinglish") {
    return [
      `Dear ${customerName} ji,`,
      "",
      `Yeh ek payment reminder hai aapke ${invoiceWordEn} ${invoiceList} ke liye.`,
      "",
      `Bakaya rashi (Due Amount): ${amount}`,
      "",
      "Kripya apni suvidha anusar jald hi payment kar dein.",
      "",
      "Dhanyawad,",
      businessName,
    ].join("\n");
  }

  // default: english
  return [
    `Dear ${customerName},`,
    "",
    `This is a gentle reminder that an amount of ${amount} is pending against ${invoiceWordEn} ${invoiceList}.`,
    "",
    "Please arrange the payment at your convenience.",
    "",
    "Thank you,",
    businessName,
  ].join("\n");
};

/**
 * Builds a wa.me link that opens WhatsApp Web/App with the message
 * pre-filled, ready to send to the given phone number.
 *
 * Assumes India (+91) when the number is exactly 10 plain digits (the
 * common case across this codebase); if it's any other length, digits are
 * passed through as-is (after stripping non-digit characters) so a number
 * that already carries a country code still works.
 */
export const buildWhatsAppReminderLink = (phone: string, message: string): string => {
  const digitsOnly = phone.replace(/\D/g, "");
  const withCountryCode = digitsOnly.length === 10 ? `91${digitsOnly}` : digitsOnly;
  return `https://wa.me/${withCountryCode}?text=${encodeURIComponent(message)}`;
};

export { fmtCurrency, fmtDateDisplay };